<template>
  <hs-modal v-model="open" :title="modalTitle" size="lg" @close="$emit('close')">
    <div class="sam-body">
      <hs-alert
        tone="info"
        message="Manual assignments record payment metadata and activate the chosen plan immediately."
      />

      <div class="sam-layout">
        <!-- Left: Plan selection -->
        <div class="sam-col">
          <p class="sam-col__label">Select Plan <span class="sam-required">*</span></p>
          <div v-if="!plans.length" class="sam-empty">
            No active subscription plans are available. Create or activate a plan first.
          </div>
          <div v-else class="sam-plans">
            <button
              v-for="plan in plans"
              :key="plan.id"
              type="button"
              class="sam-plan"
              :class="{ 'sam-plan--active': selectedPlanId === plan.id }"
              @click="selectPlan(plan.id)"
            >
              <div class="sam-plan__top">
                <div class="sam-plan__info">
                  <span class="sam-plan__name">{{ plan.display_name }}</span>
                  <span class="sam-plan__slug">{{ plan.name }}</span>
                </div>
                <span class="sam-plan__price" :class="plan.price_amount ? 'sam-plan__price--paid' : 'sam-plan__price--free'">
                  {{ formatPrice(plan.price_amount, plan.currency) }}
                </span>
              </div>
              <p v-if="plan.description" class="sam-plan__desc">{{ plan.description }}</p>
              <p class="sam-plan__billing">Billing every {{ plan.billing_period_days }} days</p>
              <div v-if="selectedPlanId === plan.id" class="sam-plan__check">
                <vue-feather type="check" size="13" />
              </div>
            </button>
          </div>
        </div>

        <!-- Right: Entity + payment -->
        <div class="sam-col">
          <p class="sam-col__label">{{ entityLabel }} <span class="sam-required">*</span></p>

          <!-- Store search -->
          <div v-if="mode === 'STORE'" class="sam-search-wrap">
            <hs-input
              v-model="storeSearch"
              icon="search"
              placeholder="Search by store name or merchant code…"
              @update:modelValue="handleStoreSearch"
            />
            <div v-if="storeResults.length" class="sam-results">
              <button
                v-for="store in storeResults"
                :key="store.id"
                type="button"
                class="sam-result"
                :class="{ 'sam-result--active': selectedEntity?.id === store.id }"
                @click="selectedEntity = store"
              >
                <div class="sam-result__main">
                  <span class="sam-result__name">{{ store.name }}</span>
                  <hs-badge tone="neutral" size="sm">#{{ store.id }}</hs-badge>
                </div>
                <span class="sam-result__sub">
                  Merchant: {{ store.merchant_code || "—" }} · Owner: {{ store.owner_name || "Unknown" }}
                </span>
              </button>
            </div>
            <p v-else class="sam-hint">Type at least 2 characters to search.</p>
          </div>

          <!-- Group search -->
          <div v-else class="sam-search-wrap">
            <hs-input
              v-model="groupSearch"
              icon="search"
              placeholder="Search groups by name…"
              @update:modelValue="handleGroupSearch"
            />
            <div v-if="groupResults.length" class="sam-results">
              <button
                v-for="group in groupResults"
                :key="group.id"
                type="button"
                class="sam-result"
                :class="{ 'sam-result--active': selectedEntity?.id === group.id }"
                @click="selectedEntity = group"
              >
                <div class="sam-result__main">
                  <span class="sam-result__name">{{ group.name }}</span>
                  <hs-badge tone="neutral" size="sm">#{{ group.id }}</hs-badge>
                </div>
                <span class="sam-result__sub">
                  Stores: {{ group.store_count ?? "—" }} · Members: {{ group.member_count ?? "—" }}
                </span>
              </button>
            </div>
            <p v-else class="sam-hint">No groups found.</p>
          </div>

          <!-- Selected entity pill -->
          <div v-if="selectedEntity" class="sam-selected-entity">
            <vue-feather type="check-circle" size="14" class="sam-selected-entity__icon" />
            <span>{{ selectedEntity.name || `#${selectedEntity.id}` }}</span>
            <button type="button" class="sam-selected-entity__clear" @click="selectedEntity = null">
              <vue-feather type="x" size="13" />
            </button>
          </div>

          <div class="sam-payment">
            <p class="sam-col__label sam-col__label--sm">Subscription dates</p>
            <hs-input v-model="form.starts_at" label="Start date and time" type="datetime-local" required />
            <p class="sam-hint">{{ selectedPlan?.billing_period_days || '—' }} days from the selected start date. Times use your local timezone.</p>
            <p v-if="expiryPreview" class="sam-hint">Expires: {{ expiryPreview }}</p>
          </div>

          <!-- Payment fields -->
          <div class="sam-payment">
            <p class="sam-col__label sam-col__label--sm">Payment Details</p>
            <div class="sam-payment__grid">
              <hs-input
                v-model.number="form.payment_amount"
                label="Amount"
                type="number"
                min="0"
                placeholder="0"
              />
              <hs-input
                v-model="form.payment_currency"
                label="Currency"
                placeholder="RWF"
                maxlength="3"
              />
            </div>
            <div class="sam-payment__grid">
              <hs-input
                v-model="form.payment_reference"
                label="Reference"
                placeholder="invoice-2025-001"
              />
            </div>
            <hs-select
              v-model="form.payment_method"
              label="Method"
              :options="paymentMethodOptions"
            />
          </div>
        </div>
      </div>

      <hs-alert v-if="localError || serverError" tone="danger" :message="localError || serverError" />
    </div>

    <template #footer>
      <div class="sam-footer">
        <hs-button variant="ghost" type="button" @click="$emit('close')">Cancel</hs-button>
        <hs-button
          variant="primary"
          icon="check-circle"
          :loading="loading"
          :disabled="!canSubmit || loading"
          @click="handleSubmit"
        >
          Assign Plan
        </hs-button>
      </div>
    </template>
  </hs-modal>
</template>

<script>
import { ref, watch, computed } from "vue";
import http from "@/helper/http";
import { HsModal, HsButton, HsInput, HsBadge, HsAlert, HsSelect } from "@/components/ui";

export default {
  name: "SubscriptionAssignmentModal",
  components: { HsModal, HsButton, HsInput, HsBadge, HsAlert, HsSelect },
  props: {
    mode: {
      type: String,
      required: true,
      validator: (value) => ["STORE", "GROUP"].includes(value),
    },
    plans: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    serverError: { type: String, default: "" },
  },
  emits: ["close", "assign"],
  setup(props, { emit }) {
    const open = ref(true);
    const selectedPlanId = ref(null);
    const selectedEntity = ref(null);
    const storeSearch = ref("");
    const storeResults = ref([]);
    const groupSearch = ref("");
    const groupResults = ref([]);
    const localError = ref("");
    const paymentMethodOptions = [
      { label: "Cash", value: "Cash" },
      { label: "Bank", value: "Bank" },
      { label: "Momo", value: "Momo" },
      { label: "Credit or Debit Card", value: "Credit or Debit Card" },
      { label: "Other", value: "Other" },
    ];

    const now = new Date();
    const localStart = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString().slice(0, 16);
    const form = ref({
      payment_amount: null,
      payment_currency: "RWF",
      payment_reference: "",
      payment_method: "Cash",
      starts_at: localStart,
    });

    const selectedPlan = computed(() => props.plans.find((plan) => plan.id === selectedPlanId.value));
    const expiryPreview = computed(() => {
      const start = new Date(form.value.starts_at);
      const days = selectedPlan.value?.billing_period_days;
      if (Number.isNaN(start.getTime()) || !days) return "";
      return new Date(start.getTime() + days * 86400000).toLocaleString();
    });

    const modalTitle = computed(() =>
      props.mode === "STORE" ? "Assign Plan to Store" : "Assign Plan to Group"
    );
    const entityLabel = computed(() =>
      props.mode === "STORE" ? "Store" : "Group"
    );
    const canSubmit = computed(
      () => Boolean(selectedPlanId.value && selectedEntity.value?.id)
    );

    watch(
      () => props.plans,
      (plans) => {
        if (plans?.length && !selectedPlanId.value) {
          selectedPlanId.value = plans[0].id;
        }
      },
      { immediate: true }
    );

    let debounceTimeout = null;
    const handleStoreSearch = () => {
      const query = storeSearch.value.trim();
      storeResults.value = [];
      if (debounceTimeout) clearTimeout(debounceTimeout);
      if (query.length < 2) return;
      debounceTimeout = setTimeout(async () => {
        try {
          const { data } = await http.get("/v1/admin/jack/stores", {
            params: { search: query, page: 1, per_page: 10 },
          });
          storeResults.value = data.items || [];
        } catch (err) {
          console.error("Failed to search stores", err);
        }
      }, 300);
    };

    const handleGroupSearch = async () => {
      const query = groupSearch.value.trim();
      try {
        const { data } = await http.get("/v2/groups", {
          params: { page: 1, per_page: 20 },
        });
        let items = data.items || [];
        if (query.length) {
          items = items.filter((g) =>
            g.name?.toLowerCase().includes(query.toLowerCase())
          );
        }
        groupResults.value = items;
      } catch (err) {
        console.error("Failed to load groups", err);
      }
    };

    watch(
      () => props.mode,
      (mode) => {
        selectedEntity.value = null;
        if (mode === "GROUP") handleGroupSearch();
      },
      { immediate: true }
    );

    const formatPrice = (amount, currency) => {
      if (!amount) return "Free";
      try {
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: currency || "RWF",
          maximumFractionDigits: 2,
        }).format(Number(amount));
      } catch {
        return `${amount} ${currency || "RWF"}`;
      }
    };

    const selectPlan = (id) => {
      selectedPlanId.value = id;
    };

    const handleSubmit = () => {
      localError.value = "";
      if (!selectedPlanId.value) {
        localError.value = props.plans.length
          ? "Select a subscription plan to continue."
          : "No active subscription plans are available. Create or activate a plan first.";
        return;
      }
      if (!selectedEntity.value?.id) {
        localError.value = `Select a ${entityLabel.value.toLowerCase()} to continue.`;
        return;
      }
      if (!form.value.starts_at || Number.isNaN(new Date(form.value.starts_at).getTime())) {
        localError.value = "Select a valid start date and time.";
        return;
      }
      emit("assign", {
        plan_id: selectedPlanId.value,
        entity_id: selectedEntity.value.id,
        payment_amount:
          form.value.payment_amount === null || form.value.payment_amount === ""
            ? null
            : Number(form.value.payment_amount),
        payment_currency: form.value.payment_currency || "RWF",
        payment_reference: form.value.payment_reference?.trim() || null,
        payment_method: form.value.payment_method?.trim() || null,
        starts_at: new Date(form.value.starts_at).toISOString(),
      });
    };

    return {
      open,
      modalTitle,
      entityLabel,
      canSubmit,
      selectedPlanId,
      selectedPlan,
      expiryPreview,
      selectedEntity,
      storeSearch,
      storeResults,
      groupSearch,
      groupResults,
      localError,
      paymentMethodOptions,
      form,
      formatPrice,
      selectPlan,
      handleStoreSearch,
      handleGroupSearch,
      handleSubmit,
    };
  },
};
</script>

<style scoped>
.sam-body {
  display: grid;
  gap: 1.1rem;
}

.sam-layout {
  display: grid;
  gap: 1.25rem;
  grid-template-columns: 1fr 1fr;
}

@media (max-width: 640px) {
  .sam-layout {
    grid-template-columns: 1fr;
  }
}

.sam-col {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  min-width: 0;
}

.sam-col__label {
  color: var(--hs-text);
  font-size: 0.83rem;
  font-weight: 750;
  margin: 0;
}

.sam-col__label--sm {
  color: var(--hs-muted);
  font-size: 0.78rem;
  font-weight: 700;
  margin-top: 0.5rem;
}

.sam-required {
  color: #ef4444;
  margin-left: 0.15rem;
}

/* Plans */
.sam-plans {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 340px;
  overflow-y: auto;
}

.sam-plan {
  background: color-mix(in srgb, var(--hs-bg-soft) 60%, var(--hs-surface));
  border: 1.5px solid var(--hs-border);
  border-radius: 12px;
  cursor: pointer;
  padding: 0.75rem 0.85rem;
  position: relative;
  text-align: left;
  transition: border-color 0.18s ease, background 0.18s ease;
  width: 100%;
}

.sam-plan:hover {
  border-color: color-mix(in srgb, var(--hs-primary) 40%, var(--hs-border));
}

.sam-plan--active {
  background: color-mix(in srgb, var(--hs-primary) 6%, var(--hs-surface));
  border-color: var(--hs-primary);
}

.sam-plan__top {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
}

.sam-plan__info {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}

.sam-plan__name {
  color: var(--hs-text);
  font-size: 0.86rem;
  font-weight: 750;
  line-height: 1.2;
}

.sam-plan__slug {
  color: var(--hs-muted);
  font-family: monospace;
  font-size: 0.72rem;
}

.sam-plan__price {
  font-size: 0.83rem;
  font-weight: 750;
  white-space: nowrap;
}

.sam-plan__price--paid {
  color: #15803d;
}

.sam-plan__price--free {
  color: var(--hs-muted);
}

.sam-plan__desc {
  color: var(--hs-muted);
  font-size: 0.76rem;
  margin: 0.3rem 0 0;
}

.sam-plan__billing {
  color: var(--hs-muted);
  font-size: 0.72rem;
  margin: 0.2rem 0 0;
}

.sam-plan__check {
  align-items: center;
  background: var(--hs-primary);
  border-radius: 50%;
  bottom: 0.6rem;
  color: #fff;
  display: flex;
  height: 1.2rem;
  justify-content: center;
  position: absolute;
  right: 0.6rem;
  width: 1.2rem;
}

/* Search */
.sam-search-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.sam-results {
  border: 1px solid var(--hs-border);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  max-height: 180px;
  overflow-y: auto;
}

.sam-result {
  border-bottom: 1px solid color-mix(in srgb, var(--hs-border) 60%, transparent);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.6rem 0.85rem;
  text-align: left;
  transition: background 0.15s ease;
  width: 100%;
}

.sam-result:last-child {
  border-bottom: none;
}

.sam-result:hover,
.sam-result--active {
  background: color-mix(in srgb, var(--hs-primary) 6%, var(--hs-surface));
}

.sam-result__main {
  align-items: center;
  display: flex;
  gap: 0.4rem;
}

.sam-result__name {
  color: var(--hs-text);
  font-size: 0.83rem;
  font-weight: 700;
}

.sam-result__sub {
  color: var(--hs-muted);
  font-size: 0.74rem;
}

.sam-hint {
  color: var(--hs-muted);
  font-size: 0.78rem;
  margin: 0;
}

/* Selected entity */
.sam-selected-entity {
  align-items: center;
  background: color-mix(in srgb, #16a34a 8%, var(--hs-surface));
  border: 1px solid color-mix(in srgb, #16a34a 25%, var(--hs-border));
  border-radius: 8px;
  color: #15803d;
  display: flex;
  font-size: 0.8rem;
  font-weight: 700;
  gap: 0.4rem;
  padding: 0.4rem 0.65rem;
}

.sam-selected-entity__icon {
  flex-shrink: 0;
}

.sam-selected-entity span {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sam-selected-entity__clear {
  color: #15803d;
  cursor: pointer;
  flex-shrink: 0;
  line-height: 1;
  opacity: 0.7;
}

.sam-selected-entity__clear:hover {
  opacity: 1;
}

/* Payment */
.sam-payment {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.sam-payment__grid {
  display: grid;
  gap: 0.55rem;
  grid-template-columns: 1fr 1fr;
}

/* Empty */
.sam-empty {
  color: var(--hs-muted);
  font-size: 0.83rem;
}

/* Footer */
.sam-footer {
  display: flex;
  gap: 0.6rem;
  justify-content: flex-end;
}
</style>
